import {
  Separator,
  Text,
  UserMenuHeader,
  UserMenuStats,
  UserMenuDetails,
  UserMenuDetailsRow,
  UserMenuItem,
  UserMenuSeparator,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'snap-design-system'
import { FiTool } from 'react-icons/fi'
import { formatIsoDate, isEmptyObject, translatePropToLabel } from '~/helpers';
import { USER_CONSTANTS } from '~/helpers/constants';
import { useUserData, useUserCredits } from '~/store/userStore'
import { useDialogActions } from '~/store/dialogStore'
import { ReportCredits } from '~/types/global';
import { Info } from 'lucide-react';
import { ConsultasInfoDialog } from './ConsultasInfoDialog';
import { cn } from '~/lib/utils';

type MenuItem = {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
};

interface UserMenuContentProps {
  menuActions?: MenuItem[] | null;
  showRemainingCredits?: boolean;
  classNameContainer?: string;
  classConsultas?: string;
  classSearchArgs?: string;
}

const UserMenuContent = ({ menuActions = null, showRemainingCredits = false, classNameContainer = "", classConsultas = "", classSearchArgs = "" }: UserMenuContentProps) => {
  const userData = useUserData();
  const { openDialog } = useDialogActions();
  const userProfile = userData?.[USER_CONSTANTS.user_data.role as keyof typeof userData] as string;
  const creditsValidateUntil = userData?.[USER_CONSTANTS.user_data.next_reset_credits as keyof typeof userData] as string;
  const userReportTypes = userData?.[USER_CONSTANTS.user_data.report_count as keyof typeof userData] as ReportCredits;
  const userCredits = useUserCredits();
  const isUserStandalone = userProfile === USER_CONSTANTS.profile_types.standalone;

  const getConsultasRealizadas = () => {
    const userReportsCount = userData?.[USER_CONSTANTS.user_data.report_count as keyof typeof userData] || {};
    const reportSum = Object.values(userReportsCount || {}).reduce((acc, cur) => acc + cur, 0);
    return reportSum;
  };

  const renderConsultasInfo = () => {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger className="cursor-help" asChild>
          <Info size={14} className="text-accent-500" />
        </TooltipTrigger>
        <TooltipContent side="bottom" variant="chamfered" chamfer="bottomRight">
          <div className="px-4 py-2">
            <Text className="text-left">
              Você possui <span className="font-bold"><span className="text-accent-500">{`${!isUserStandalone ? "*" : ""}`}</span>{` ${userCredits} consultas `}</span>
            </Text>
            {
              creditsValidateUntil ? (
                <Text className="text-left">
                  válidas até a data <span className="font-semibold">{formatIsoDate(creditsValidateUntil, true)}</span>.
                </Text>
              ) : null
            }
            {
              !isUserStandalone && (
                <div>
                  <Separator className="my-2 border-border" />
                  <Text className="text-left">
                    <span className="font-bold text-accent-500">*</span> {`Saiba mais sobre as consultas disponíveis `}
                    <span className="text-accent-500 underline cursor-pointer" onClick={handleOpenConsultasInfo} title="Abrir mais informações de consultas">
                      clicando aqui.
                    </span>
                  </Text>
                </div>
              )
            }
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  const handleOpenConsultasInfo = () => {
    openDialog({
      title: "INFORMAÇÕES SOBRE CONSULTAS",
      content: <ConsultasInfoDialog.Content />,
      footer: <ConsultasInfoDialog.Footer />,
      className: "max-w-xl",
    });
  };

  return (
    <div>
      {/* tag tipo perfil */}
      <UserMenuHeader icon={<FiTool size={20} />} text={userProfile || "Não Informado"} className='capitalize' />

      {/* consultas disponíveis */}
      {showRemainingCredits ? (
        <div className={`px-[18px] py-[14px] ${classNameContainer}`}>
          <div className="flex items-center gap-2 justify-between">
            <div className="flex flex-col items-start gap-0.5">
              <div className="flex items-center gap-2">
                <Text variant="body-lg" className="font-semibold">Consultas disponíveis</Text>
                {renderConsultasInfo()}
              </div>
              {creditsValidateUntil ? (
                <Text variant="body-sm" className="opacity-80">{`Válidas até ${formatIsoDate(creditsValidateUntil, true)}`}</Text>
              ) : null}
            </div>
            <Text variant="title-lg" className="font-semibold">{userCredits || 0}</Text>
          </div>
        </div>
      ) : null}

      {/* consultas realizadas */}
      <div className={cn("p-4", classConsultas)}>
        <div className="flex items-center justify-between p-1 bg-accent-500 rounded-xs">
          <Text className='font-bold text-white'>Consultas realizadas:</Text>
          <Text className='font-bold text-white'>{getConsultasRealizadas()}</Text>
        </div>
      </div>

      {/* tipos de relatório */}
      <div className={cn("px-4 pb-4", classSearchArgs)}>
        <UserMenuDetails>
          {userReportTypes && !isEmptyObject(userReportTypes) ? (
            Object.entries(userReportTypes)?.map(([key, value], index) => (
              <UserMenuDetailsRow
                key={index}
                label={translatePropToLabel(key).toUpperCase()}
                value={value.toString()}
                className='bg-primary-200'
              />
            ))
          ) : (
            <div className="opacity-80 text-sm">
              Nenhum tipo de relatório permitido.
            </div>
          )}
        </UserMenuDetails>
      </div>

      {/* menu actions */}
      {menuActions ? (
        <div className="px-4 pb-[15px]">
          {menuActions.map((item, index) => (
            <div key={index}>
              <UserMenuItem
                icon={item.icon}
                label={item.label}
                onClick={item.onClick}
              />
              {index < menuActions.length - 1 && <UserMenuSeparator />}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default UserMenuContent